import React, { useEffect, useMemo, useState } from 'react';
import { Card, Container } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Loading from '~/Components/Loading';
import Filtros from './components/Filtros';
import GridAcordoJudicial from './components/GridAcordoJudicial';
import Totalizadores from './components/Totalizadores';
import {
  consultarAcordoJudicial,
  listarCidades,
  listarCobradores,
  listarEmpreendimentos,
  listarGruposEmpresa,
} from './services/acordoJudicialService';

/**
 * Consulta Acordo Judicial (Cobrador/Imob) — read-only single-grid.
 * Fonte Delphi: UConsultaJudicial.pas (TFConsultaJudicial). RBAC server-side: o backend nega quem
 * não tem chars 95/125/126 e escopa o combo de cobradores (pezao/sojuris). Filtros todos opcionais;
 * data de corte (ontem 23:59:59) resolvida no backend. Sem Redux (consulta isolada).
 */
const ConsultaAcordoJudicial = () => {
  const [ges, setGes] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [cobradores, setCobradores] = useState([]);

  const [geSelecionado, setGeSelecionado] = useState(null);
  const [cidadeSelecionada, setCidadeSelecionada] = useState(null);
  const [empreendimentoSelecionado, setEmpreendimentoSelecionado] = useState(
    null
  );
  const [cobradorSelecionado, setCobradorSelecionado] = useState(null);

  const [itens, setItens] = useState([]);
  const [buscou, setBuscou] = useState(false);
  const [carregandoCombos, setCarregandoCombos] = useState(true);
  const [loading, setLoading] = useState(false);

  const tratarErro = (err, fallback) => {
    const msg =
      (err && err.message) ||
      fallback;
    toast.error(msg);
  };

  // Mount: combos GE / Cidade / Empreendimento / Cobrador em paralelo.
  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const [resGes, resCidades, resEmps, resCobradores] = await Promise.all([
          listarGruposEmpresa(),
          listarCidades(),
          listarEmpreendimentos(),
          listarCobradores(),
        ]);
        if (!ativo) return;
        setGes(resGes || []);
        setCidades(resCidades || []);
        setEmpreendimentos(resEmps || []);
        setCobradores(resCobradores || []);
      } catch (err) {
        if (ativo) tratarErro(err, 'Erro ao carregar filtros.');
      } finally {
        if (ativo) setCarregandoCombos(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, []);

  // RN-14: ao trocar a GE, re-busca o combo de Cobrador filtrado pela GE nova.
  const handleChangeGe = async novaGe => {
    setGeSelecionado(novaGe);
    setCobradorSelecionado(null);
    try {
      const res = await listarCobradores(novaGe);
      setCobradores(res || []);
    } catch (err) {
      tratarErro(err, 'Erro ao carregar cobradores.');
    }
  };

  const handleConsultar = async () => {
    setLoading(true);
    try {
      const res = await consultarAcordoJudicial({
        ge: geSelecionado,
        cidade: cidadeSelecionada,
        empreendimento: empreendimentoSelecionado,
        cobrador: cobradorSelecionado,
      });
      setItens(res || []);
      setBuscou(true);
    } catch (err) {
      tratarErro(err, 'Erro ao consultar acordos judiciais.');
    } finally {
      setLoading(false);
    }
  };

  // Limpar (paridade BitBtn2Click): zera filtros + grid.
  const handleLimpar = () => {
    setGeSelecionado(null);
    setCidadeSelecionada(null);
    setEmpreendimentoSelecionado(null);
    setCobradorSelecionado(null);
    setItens([]);
    setBuscou(false);
  };

  // Totalizadores (RN-15) — derivados das linhas, como o loop do Delphi (:234-242).
  const totais = useMemo(() => {
    const qtdeContratos = itens.length;
    const qtdeParcelas = itens.reduce(
      (acc, it) => acc + Number(it.parc || 0),
      0
    );
    const valorTotal = itens.reduce(
      (acc, it) => acc + Number(it.valor || 0),
      0
    );
    return { qtdeContratos, qtdeParcelas, valorTotal };
  }, [itens]);

  return (
    <Container fluid className="mt-3">
      <Card>
        <Card.Header>
          <h5 className="mb-0">Consulta Acordo Judicial</h5>
        </Card.Header>
        <Card.Body>
          {carregandoCombos ? (
            <div className="text-center my-4">
              <Loading />
            </div>
          ) : (
            <>
              <Filtros
                ges={ges}
                cidades={cidades}
                empreendimentos={empreendimentos}
                cobradores={cobradores}
                geSelecionado={geSelecionado}
                cidadeSelecionada={cidadeSelecionada}
                empreendimentoSelecionado={empreendimentoSelecionado}
                cobradorSelecionado={cobradorSelecionado}
                onChangeGe={handleChangeGe}
                onChangeCidade={setCidadeSelecionada}
                onChangeEmpreendimento={setEmpreendimentoSelecionado}
                onChangeCobrador={setCobradorSelecionado}
                onConsultar={handleConsultar}
                onLimpar={handleLimpar}
                loading={loading}
              />

              {loading ? (
                <div className="text-center my-4">
                  <Loading />
                </div>
              ) : buscou && itens.length === 0 ? (
                <div className="text-center text-muted my-4">
                  Nenhum registro encontrado!
                </div>
              ) : (
                <>
                  <div className="mt-3">
                    <GridAcordoJudicial itens={itens} />
                  </div>
                  <Totalizadores
                    qtdeContratos={totais.qtdeContratos}
                    qtdeParcelas={totais.qtdeParcelas}
                    valorTotal={totais.valorTotal}
                  />
                </>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ConsultaAcordoJudicial;
